import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '@environments/environment';
import { AngularPaths, ContentType, MessageType, Response } from '@models/index.model';
import { SpConfig } from '@models/user/user.model';
import { TranslateService } from '@ngx-translate/core';
import { FeedMessageService, UtilsService, LoadingService, SigntrustService, UserService, showError, showErrorAndWarning, showWarning, SessionService } from '@services/index.service';
import { Filesystem, ReadFileResult } from '@capacitor/filesystem';
import fetch from 'cross-fetch';
import { catchError, filter, from, map, Observable, of, Subject, switchMap, takeUntil } from 'rxjs';
import { SharePointPdfGraphService } from '../../msgraph/sharepoint-pdf-graph.service';
import { GraphAuthService, GraphInteractionRequiredError } from '../../msgraph/graph-auth.service';
import { MsalRuntimeConfigService } from '../../msgraph/msal-runtime-config.service';

@Component({
  selector: 'app-web-sign',
  templateUrl: './web-sign.component.html',
  styleUrls: ['./web-sign.component.scss'],
  standalone: false,
})
export class WebSignComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('signViewTemplate') _signViewTemplate!: TemplateRef<any>;
  @ViewChild('fileDropRef') _fileDropRef!: ElementRef<any>;
  public theme = environment.THEME;
  public documentFile: File | null = null;
  public page = 1;
  public isSharepointDocument = false;
  public isMobile = false;
  public title!: string;
  public cancelProcessMsg!: string;
  private _overlayRef!: OverlayRef;
  private _portal!: TemplatePortal;
  private readonly _destroying$ = new Subject<void>();
  private readonly _destroy$ = new Subject<void>();
  // remote state
  public oldFileInfo: any;
  public spConfig!: (SpConfig & {
    driveId?: string;
    driveItemId?: string;
    mode?: 'new' | 'rewrite';
    suffix?: string;
    parentRefId?: string;
  });
  public agentConfig: any;
  public needsMicrosoftLogin = false;

  constructor(
    private readonly translate: TranslateService,
    private readonly _titleService: Title,
    private readonly _overlay: Overlay,
    private readonly _viewContainerRef: ViewContainerRef,
    private readonly _notify: FeedMessageService,
    public readonly _utils: UtilsService,
    private readonly _loading: LoadingService,
    private readonly _user: UserService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly _signtrustServ: SigntrustService,
    private readonly _dialog: MatDialog,
    private readonly ngZone: NgZone,
    private readonly spGraph: SharePointPdfGraphService,
    private readonly graphAuth: GraphAuthService,
    private readonly spRuntimeCfg: MsalRuntimeConfigService,
    private readonly _sessionServ: SessionService
  ) {
    this.theme = this._utils.titleCaseWord(this.theme);
    this._loading.showGlobalLoading();
  }

  public async ngOnInit(): Promise<void> {
    this.translate.get('NAVBAR.PAGES.WEB-SIGN.TITLE').subscribe((res) => {
      this._titleService.setTitle(`${res} - ${this.theme}`);
      this.title = res;
    });
    this.translate.get('GLOBAL.ARE-YOU-SURE-CANCEL').subscribe((res) => {
      this.cancelProcessMsg = res;
    });
    await this.graphAuth.processRedirectResultSafe();

    // params
    this.route.params.subscribe((params: Params) => {
      if (params.spConfig || params.agentConfig) {
        this.initRemoteSigntrustAction(params);
      } else {
        // Red de seguridad: si venimos de un refresh/login y el hash de un solo
        // uso sigue pendiente (no consumido con éxito), retomamos el flujo con él.
        const pendingHash = this.getRemoteSignHash();
        if (pendingHash?.hash) {
          this.initRemoteSigntrustAction(
            pendingHash.kind === 'agent'
              ? { agentConfig: pendingHash.hash }
              : { spConfig: pendingHash.hash }
          );
        } else {
          this._loading.hideGlobalLoading();
        }
      }
    });
    if (environment.platform === 'mobile') {
      this.isMobile = true;
      const appUrlOpen = localStorage.getItem('appUrlOpen');
      if (appUrlOpen) {
        const file = this._signtrustServ.getFile().subscribe();
        if (file) {
          localStorage.removeItem('appUrlOpen');
        } else {
          this._signtrustServ.setFile(appUrlOpen);
          window.location.reload();
        }
      }
      try {
        this._signtrustServ
          .getFile()
          .pipe(
            takeUntil(this._destroy$),
            filter((fileUrl) => environment.platform === 'mobile' && !!fileUrl),
            switchMap((fileUrl: string) =>
              from(
                Filesystem.readFile({
                  path: fileUrl,
                })
              )
            ),
            switchMap((readFileResult: ReadFileResult) => {
              const uri = 'data:application/pdf;base64,' + readFileResult.data;
              return from(fetch(uri)).pipe(switchMap((res: any) => res.blob()));
            })
          )
          .subscribe((fileBlob: Blob) => {
            const fileName = 'fileName.pdf';
            const file = new File([fileBlob], fileName, { type: ContentType.pdfType });
            this.ngZone.run(() => {
              if (localStorage.getItem('docOpen') === '1') {
                if (confirm(this.cancelProcessMsg) === true) {
                  this.onClosePdfViewer();
                  this.onFileUploaded(file, false);
                }
              } else {
                this.onClosePdfViewer();
                this.onFileUploaded(file, false);
              }
            });
          });
      } catch {
        this._loading.hideGlobalLoading();
      }
    }
  }

  public ngAfterViewInit(): void {
    this._portal = new TemplatePortal(this._signViewTemplate, this._viewContainerRef);
    this._overlayRef = this._overlay.create({
      positionStrategy: this._overlay.position().global().centerHorizontally().centerVertically(),
      hasBackdrop: false,
    });
  }

  public ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
    this._dialog.closeAll();
    this._destroy$.next();
    this._destroy$.complete();
  }

  // ============================================================
  // Helpers
  // ============================================================

  private hasAuthApiSession(): boolean {
    return this._sessionServ.isLoggedIn() && this._sessionServ.checkTokenValid();
  }


  // ============================================================
  // SharePoint/MSAL flow state (evitar reusar spConfig de un solo uso y evitar bucles)
  // ============================================================
  private readonly SP_PENDING_KEY = 'spPendingGraphDownload';
  private readonly SP_MSAL_RETRY_KEY = 'spMsalRetryState';

  private setSpPendingDownload(payload: {
    driveId: string;
    driveItemId: string;
    spConfigId?: string;
    ts?: number;
    mode?: 'new' | 'rewrite';
    suffix?: string;
    parentRefId?: string;
  }) {
    const data = { ...payload, ts: payload.ts ?? Date.now() };
    sessionStorage.setItem(this.SP_PENDING_KEY, JSON.stringify(data));
  }

  private getSpPendingDownload():
    | {
      driveId: string;
      driveItemId: string;
      spConfigId?: string;
      ts?: number;
      mode?: 'new' | 'rewrite';
      suffix?: string;
      parentRefId?: string;
    }
    | null {
    try {
      const raw = sessionStorage.getItem(this.SP_PENDING_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private clearSpPendingDownload() {
    sessionStorage.removeItem(this.SP_PENDING_KEY);
  }

  private canRetryMicrosoftAuth(): boolean {
    // Evitar bucles de redirects si hay un problema de acceso/consent o configuración.
    // Máximo: 2 redirecciones en una ventana de 3 minutos.
    const now = Date.now();
    try {
      const raw = sessionStorage.getItem(this.SP_MSAL_RETRY_KEY);
      const state = raw ? JSON.parse(raw) : { count: 0, firstTs: now };
      const windowMs = 3 * 60 * 1000;

      if (now - (state.firstTs ?? now) > windowMs) {
        sessionStorage.setItem(this.SP_MSAL_RETRY_KEY, JSON.stringify({ count: 0, firstTs: now }));
        return true;
      }
      return (state.count ?? 0) < 2;
    } catch {
      return true;
    }
  }

  private bumpMicrosoftAuthRetry(): void {
    const now = Date.now();
    try {
      const raw = sessionStorage.getItem(this.SP_MSAL_RETRY_KEY);
      const state = raw ? JSON.parse(raw) : { count: 0, firstTs: now };
      const next = { count: (state.count ?? 0) + 1, firstTs: state.firstTs ?? now };
      sessionStorage.setItem(this.SP_MSAL_RETRY_KEY, JSON.stringify(next));
    } catch {
      sessionStorage.setItem(this.SP_MSAL_RETRY_KEY, JSON.stringify({ count: 1, firstTs: now }));
    }
  }

  private resetMicrosoftAuthRetry(): void {
    sessionStorage.removeItem(this.SP_MSAL_RETRY_KEY);
  }

  // ============================================================
  // Hash de firma remota de un solo uso
  // (se guarda ANTES de consumirlo y se borra SOLO tras consumo con éxito)
  // ============================================================
  private readonly SP_REMOTE_HASH_KEY = 'spRemoteSignHash';

  private saveRemoteSignHash(kind: 'sp' | 'agent', hash: string): void {
    localStorage.setItem(this.SP_REMOTE_HASH_KEY, JSON.stringify({ kind, hash, ts: Date.now() }));
  }

  private getRemoteSignHash(): { kind: 'sp' | 'agent'; hash: string; ts: number } | null {
    try {
      const raw = localStorage.getItem(this.SP_REMOTE_HASH_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private clearRemoteSignHash(): void {
    localStorage.removeItem(this.SP_REMOTE_HASH_KEY);
  }

  /**
   * Garantiza una sesión de app usable ANTES de consumir el hash de un solo uso:
   *  - token vivo -> true
   *  - token caducado con refreshToken -> refresco silencioso -> true
   *  - refreshToken inválido o inexistente -> guarda el hash, va a login y devuelve false
   * En false, el flujo se retoma al volver (routerRedirectBeforeLogin re-navega con el hash).
   */
  private ensureAuthApiSession$(kind: 'sp' | 'agent', hash: string): Observable<boolean> {
    if (this._sessionServ.isLoggedIn() && this._sessionServ.checkTokenValid()) {
      // eslint-disable-next-line no-console
      // console.debug(`[refresh-debug] WebSignComponent.ensureAuthApiSession$:${kind} -> sesión ya válida, no refresca`);
      return of(true);
    }

    if (this._sessionServ.hasRefreshToken()) {
      // eslint-disable-next-line no-console
      // console.debug(`[refresh-debug] WebSignComponent.ensureAuthApiSession$:${kind} -> sesión caducada, dispara refresh`);
      return this._sessionServ.refreshSessionShared({ source: `WebSignComponent.ensureAuthApiSession$:${kind}` }).pipe(
        map(() => true),
        catchError(() => {
          this.redirectToLoginKeepingHash(kind, hash);
          return of(false);
        })
      );
    }

    this.redirectToLoginKeepingHash(kind, hash);
    return of(false);
  }

  private redirectToLoginKeepingHash(kind: 'sp' | 'agent', hash: string): void {
    // Clave que lee SessionService.routerRedirectBeforeLogin tras el login.
    localStorage.setItem(kind === 'sp' ? 'spRedirectId' : 'agentRedirectId', hash);
    this._loading.hideGlobalLoading();
    this.router.navigate([`/${AngularPaths.LOGIN}`], { replaceUrl: true });
  }

  // ============================================================
  // Remote init (SharePoint / Agent)
  // ============================================================

  public initRemoteSigntrustAction(params: any) {
    if (params.spConfig) {
      this.initSharePointRemoteFlow(params.spConfig);
      return;
    }
    if (params.agentConfig) {
      this.initAgentRemoteFlow(params.agentConfig);
      return;
    }
    this._loading.hideGlobalLoading();
  }

  private initSharePointRemoteFlow(spConfigParam: string) {
    try {
      // 1) Guardar el hash de un solo uso ANTES de consumirlo.
      this.saveRemoteSignHash('sp', spConfigParam);
      localStorage.setItem('lastRemoteSignId', spConfigParam);

      // Si el backend no ha configurado SharePoint, no intentamos MSAL/Graph.
      if (!this.spRuntimeCfg.getClientId()) {
        this._notify.feedBackResult(
          MessageType.WARNING,
          this.translate.instant('WEB-SIGN.FILE-UPLOAD.SP-NOT-CONFIGURED'),
          showWarning
        );
        this._loading.hideGlobalLoading();
        return;
      }

      // 2) Asegurar sesión de app: token vivo -> seguimos; caducado con
      //    refreshToken -> refresco silencioso; sin refreshToken -> login
      //    (el flujo se retoma con el hash al volver).
      this.ensureAuthApiSession$('sp', spConfigParam).subscribe({
        next: (ok) => {
          if (!ok) return; // se ha redirigido a login; se retomará al volver
          this.resumeSharePointConsumption(spConfigParam);
        },
        error: () => this._loading.hideGlobalLoading(),
      });
    } catch (error: any) {
      console.error(error);
      this._loading.hideGlobalLoading();
    }
  }

  private resumeSharePointConsumption(spConfigParam: string) {
    // Si ya consumimos y volvemos de un redirect MSAL, retomar sin re-consumir.
    const pending = this.getSpPendingDownload();
    if (pending?.spConfigId === spConfigParam && pending.driveId && pending.driveItemId) {
      this.spConfig = {
        ...(this.spConfig ?? {}),
        driveId: pending.driveId,
        driveItemId: pending.driveItemId,
        mode: pending.mode,
        suffix: pending.suffix,
        parentRefId: pending.parentRefId,
      } as any;
      this.isSharepointDocument = true;
      this.loadPdfFromSharePoint(pending.driveId, pending.driveItemId);
      return;
    }

    // 3) Consumo del hash de un solo uso.
    this._signtrustServ.getRemoteDocument(spConfigParam).subscribe({
      next: async (res: Response<any>) => {
        if (!res.data) {
          this._loading.hideGlobalLoading();
          return;
        }
        this.spConfig = this._utils.parseUserPreferenceConfig(res.data);
        if (!this.spConfig?.driveId || !this.spConfig?.driveItemId) {
          this._loading.hideGlobalLoading();
          return;
        }
        await this.loadPdfFromSharePoint(this.spConfig.driveId, this.spConfig.driveItemId);
      },
      error: (error: any) => {
        console.error(error);
        // El hash NO se limpia aquí: solo se borra tras un consumo con éxito.
        this._loading.hideGlobalLoading();
      },
    });
  }

  private initAgentRemoteFlow(agentConfigParam: string) {
    try {
      // 1) Guardar el hash de un solo uso ANTES de consumirlo.
      this.saveRemoteSignHash('agent', agentConfigParam);
      localStorage.setItem('lastRemoteSignId', agentConfigParam);

      // 2) Asegurar sesión de app (silenciosa si es posible).
      this.ensureAuthApiSession$('agent', agentConfigParam).subscribe({
        next: (ok) => {
          if (!ok) return;
          this.resumeAgentConsumption(agentConfigParam);
        },
        error: () => this._loading.hideGlobalLoading(),
      });
    } catch (error: any) {
      console.error(error);
      this._loading.hideGlobalLoading();
    }
  }

  private resumeAgentConsumption(agentConfigParam: string) {
    // 3) Consumo del hash de un solo uso.
    this._signtrustServ.getRemoteDocument(agentConfigParam).subscribe({
      next: (res: Response<any>) => {
        if (!res.data) {
          this._loading.hideGlobalLoading();
          return;
        }
        this.agentConfig = this._utils.parseUserPreferenceConfig(res.data);
        if (this.agentConfig) {
          this.getDocumentFromAgent(this.agentConfig);
        } else {
          this._loading.hideGlobalLoading();
        }
      },
      error: (error: any) => {
        console.error(error);
        // El hash NO se limpia aquí: solo se borra tras un consumo con éxito.
        this._loading.hideGlobalLoading();
      },
    });
  }

  // ============================================================
  // Agent download
  // ============================================================

  public getDocumentFromAgent(agentConfig: any) {
    if (!agentConfig?.endpointUri) {
      this._loading.hideGlobalLoading();
      return;
    }
    this._user.getDocumentToSign(agentConfig.endpointUri, agentConfig.documentId).subscribe(
      (res: any) => {
        if (environment.production) {
          window.history.replaceState({}, '', `${window.location.origin}/personal/${AngularPaths.SIGNTRUST}`);
        } else {
          window.history.replaceState({}, '', `${window.location.origin}/${AngularPaths.SIGNTRUST}`);
        }
        if (res.body) {
          const contentDispHeader: string = res.headers.get('Content-Disposition');
          let filename = 'document.pdf';
          if (contentDispHeader) {
            const parts = /filename=([^;]+)/gi.exec(contentDispHeader);
            filename = parts?.[1] || 'document.pdf';
            filename = decodeURIComponent(filename);
          }
          const blob = new Blob([res.body], { type: ContentType.pdfType });
          const agentDocumentFile = new File([blob], filename, { type: ContentType.pdfType });
          this.onFileUploaded(agentDocumentFile, false);
        }
      },
      (error: any) => {
        console.error(error);
        this._notify.feedBackResult(MessageType.ERROR, this.translate.instant('WEB-SIGN.SIGN-IMG.LABELS.AGENT-DOCUMENT-ERROR'));
        this._loading.hideGlobalLoading();
        this.onClosePdfViewer();
      }
    );
  }

  // ============================================================
  // SharePoint (Graph)
  // ============================================================

  private async loadPdfFromSharePoint(driveId: string, driveItemId: string) {
    this._loading.showGlobalLoading();
    this.needsMicrosoftLogin = false;

    try {
      await this.graphAuth.handleRedirectIfPresent();
      const { blob, fileName } = await this.spGraph.downloadPdfBlob(driveId, driveItemId);
      this.clearSpPendingDownload();
      this.resetMicrosoftAuthRetry();
      this.onFileUploaded(new File([blob], fileName, { type: ContentType.pdfType }), true);
    } catch (e: any) {
      const interactionRequired =
        e instanceof GraphInteractionRequiredError || String(e?.message) === 'MICROSOFT_INTERACTION_REQUIRED';
      // No mostramos errores de Microsoft al usuario final. Reintentamos auth de forma controlada.
      if (interactionRequired) {
        this.needsMicrosoftLogin = true;
        const spConfigId = this.route.snapshot?.params?.spConfig;
        this.startMicrosoftAuthForSharePoint({ driveId, driveItemId, spConfigId });
        return;
      }
      console.error(e);
      this._notify.feedBackResult(MessageType.ERROR, this.translate.instant('WEB-SIGN.FILE-UPLOAD.SP-DOWNLOAD-FAILED'));
    } finally {
      this._loading.hideGlobalLoading();
    }
  }

  private startMicrosoftAuthForSharePoint(ctx: { driveId: string; driveItemId: string; spConfigId?: string }) {
    // Evitar loops: máximo 2 redirects en una ventana de 3 minutos.
    if (!this.canRetryMicrosoftAuth()) {
      this.needsMicrosoftLogin = false;
      this._notify.feedBackResult(
        MessageType.WARNING,
        this.translate.instant('WEB-SIGN.FILE-UPLOAD.SP-AUTH-RETRY-STOP'),
        showWarning
      );
      return;
    }
    this.setSpPendingDownload({
      spConfigId: ctx.spConfigId,
      driveId: ctx.driveId,
      driveItemId: ctx.driveItemId,
      mode: this.spConfig?.mode,
      suffix: this.spConfig?.suffix,
      parentRefId: this.spConfig?.parentRefId,
    });

    this.bumpMicrosoftAuthRetry();
    localStorage.setItem('msalReturnUrl', window.location.href);
    this.needsMicrosoftLogin = false;
    try {
      this.graphAuth.acquireTokenRedirect();
    } catch {
      this.graphAuth.loginRedirect();
    }
  }

  public async uploadDocument(file: File) {
    if (!file) return;
    try {
      this._loading.showGlobalLoading();
      this.documentFile = file;
      if (this.isSharepointDocument && this.spConfig?.driveId && this.spConfig?.driveItemId) {
        await this.graphAuth.handleRedirectIfPresent();
        const mode = this.spConfig.mode ?? 'new';
        const targetName =
          mode === 'new' ? this.spGraph.buildTargetFileName(file.name, this.spConfig.suffix) : undefined;
        await this.spGraph.uploadPdfBlob(this.spConfig.driveId, this.spConfig.driveItemId, file, mode, targetName);
        const shownName = mode === 'new' ? (targetName ?? file.name) : file.name;
        this._notify.feedBackResult(MessageType.SUCCESS, `${this.translate.instant('WEB-SIGN.FILE-UPLOAD.SP-UPLOAD-MSG')} ${shownName}`);
        this._loading.hideGlobalLoading();
        return;
      }
      this._loading.hideGlobalLoading();
    } catch (e: any) {
      const interactionRequired =
        e instanceof GraphInteractionRequiredError || String(e?.message) === 'MICROSOFT_INTERACTION_REQUIRED';

      if (interactionRequired) {
        if (!this.canRetryMicrosoftAuth()) {
          this.needsMicrosoftLogin = false;
          this._notify.feedBackResult(MessageType.WARNING, this.translate.instant('WEB-SIGN.FILE-UPLOAD.SP-AUTH-RETRY-STOP'));
          this._loading.hideGlobalLoading();
          return;
        }

        this.bumpMicrosoftAuthRetry();
        localStorage.setItem('msalReturnUrl', window.location.href);
        this.needsMicrosoftLogin = false;

        try {
          this.graphAuth.acquireTokenRedirect();
        } catch {
          this.graphAuth.loginRedirect();
        }
        return;
      }

      console.error(e);
      this._notify.feedBackResult(
        MessageType.ERROR,
        this.translate.instant('WEB-SIGN.FILE-UPLOAD.SP-UPLOAD-FAILED'),
        showError
      );
      this._loading.hideGlobalLoading();
    }
  }

  // ============================================================
  // Local file upload / viewer open
  // ============================================================

  public onFileUploaded(file: File, isSp?: boolean) {
    try {
      this._setDocumentFile(file, isSp);
    } catch (error: any) {
      this._notify.feedBackResult(MessageType.ERROR, error.message, showError);
    }
    this._loading.hideGlobalLoading();
  }

  private _setDocumentFile(documentFile: File, isSp?: boolean) {
    const bytesToMb = 0.00000095367432;
    if (documentFile.size * bytesToMb >= 150) {
      throw new Error(this.translate.instant('GLOBAL.ERRORS.FILESIZE150MB'));
    }
    const fileTypePDF = ContentType.pdfType;
    if (documentFile.type !== fileTypePDF) {
      throw new Error(this.translate.instant('GLOBAL.ERRORS.FILETYPEPDF'));
    }
    this.documentFile = documentFile;
    this.isSharepointDocument = !!isSp;
    // Consumo con éxito: ahora sí borramos el hash de un solo uso.
    this.clearRemoteSignHash();
    this.remoteSignStorageCleanup();
    this.openPdfViewer();
    localStorage.setItem('docOpen', '1');
  }

  private remoteSignStorageCleanup() {
    if (localStorage.getItem('spRedirectId')) this._utils.removeLocalStorageItem('spRedirectId');
    if (localStorage.getItem('agentRedirectId')) this._utils.removeLocalStorageItem('agentRedirectId');
    if (localStorage.getItem('lastRemoteSignId')) this._utils.removeLocalStorageItem('lastRemoteSignId');
    // limpiar url
    if (environment.production) {
      window.history.replaceState({}, '', `${window.location.origin}/personal/${AngularPaths.SIGNTRUST}`);
    } else {
      window.history.replaceState({}, '', `${window.location.origin}/${AngularPaths.SIGNTRUST}`);
    }
    this._loading.hideGlobalLoading();
  }

  public openPdfViewer() {
    if (this._overlayRef?.hasAttached()) {
      this.onClosePdfViewer();
    }
    this._overlayRef.attach(this._portal);
  }

  public onClosePdfViewer() {
    localStorage.setItem('docOpen', '0');
    this.remoteSignStorageCleanup();
    this._dialog.closeAll();
    this.documentFile = null;
    this.router.navigate([`${AngularPaths.SIGNTRUST}`]);
    this._overlayRef.detach();
  }
}
